import React, { useState, useEffect } from 'react';
import { portalApi } from '../utils/api';
import { formatCurrency } from '../utils/helpers';

function PortalOrders() {
  const [orders, setOrders] = useState([]);
  const [loading, setLoading] = useState(true);
  const [processing, setProcessing] = useState(null);
  const [message, setMessage] = useState(null);

  useEffect(() => {
    loadOrders();
  }, []);

  const loadOrders = async () => {
    try {
      setLoading(true);
      const data = await portalApi.getOrders();
      setOrders(data || []);
    } catch (err) {
      console.error('Error loading orders:', err);
      setMessage({ type: 'error', text: err.message });
    } finally {
      setLoading(false);
    }
  };

  const simulateOrder = async (platform) => {
    const mockOrders = {
      lieferando: {
        platform: 'lieferando',
        orderId: `LFD-${Math.random().toString(36).substr(2, 8).toUpperCase()}`,
        customer: { name: 'Max Mustermann', address: 'Teststraße 1, 10115 Berlin', phone: '+49301234567' },
        items: [
          { sku: 'PIZZA-001', name: 'Pizza Margherita', qty: 2, priceNet: 8.40, vatRate: 7.0 },
          { sku: 'COLA-001', name: 'Cola 0.33l', qty: 1, priceNet: 2.52, vatRate: 19.0 }
        ],
        fees: [
          { name: 'Lieferando Provision (30%)', amount: -5.46, vatRate: 19.0 },
          { name: 'Liefergebühr', amount: 2.99, vatRate: 19.0 }
        ],
        tip: { amount: 2.00, vatRate: 19.0 }
      },
      wolt: {
        platform: 'wolt',
        orderId: `WOLT-${Math.random().toString(36).substr(2, 8).toUpperCase()}`,
        customer: { name: 'Anna Schmidt', address: 'Beispielweg 5, 10117 Berlin', phone: '+49307654321' },
        items: [
          { sku: 'PASTA-001', name: 'Pasta Carbonara', qty: 1, priceNet: 10.28, vatRate: 7.0 },
          { sku: 'BEER-001', name: 'Bier 0.5l', qty: 2, priceNet: 3.36, vatRate: 19.0 }
        ],
        fees: [
          { name: 'Wolt Provision (25%)', amount: -4.25, vatRate: 19.0 }
        ],
        tip: { amount: 1.50, vatRate: 19.0 }
      }
    };

    try {
      const orderData = mockOrders[platform];
      await portalApi.webhook(orderData);
      setMessage({ type: 'success', text: `${platform} Bestellung simuliert!` });
      loadOrders();
      setTimeout(() => setMessage(null), 3000);
    } catch (err) {
      setMessage({ type: 'error', text: err.message });
    }
  };

  const acceptOrder = async (orderId) => {
    try {
      setProcessing(orderId);
      await portalApi.acceptOrder(orderId);
      setMessage({ type: 'success', text: 'Bestellung angenommen und fiskalisiert!' });
      loadOrders();
      setTimeout(() => setMessage(null), 3000);
    } catch (err) {
      setMessage({ type: 'error', text: err.message });
    } finally {
      setProcessing(null);
    }
  };

  const getPlatformBadge = (platform) => {
    const colors = {
      lieferando: 'bg-[#ff8000]/10 text-[#ff8000]',
      wolt: 'bg-[#00c2e8]/10 text-[#00c2e8]',
      uber_eats: 'bg-[#06c167]/10 text-[#06c167]'
    };
    return (
      <span className={`px-2 py-1 rounded-full text-xs font-medium ${colors[platform] || 'bg-gray-100 text-gray-800'}`}>
        {platform}
      </span>
    );
  };

  return (
    <div>
      <div className="flex items-center justify-between mb-6">
        <div>
          <h1 className="text-3xl font-bold" style={{ fontFamily: 'Chivo, sans-serif' }}>
            Portal-Bestellungen
          </h1>
          <p className="text-[hsl(var(--muted-foreground))] mt-1">
            Lieferando, Wolt, Uber Eats
          </p>
        </div>
        <div className="flex space-x-2">
          <button
            onClick={() => simulateOrder('lieferando')}
            className="px-3 py-2 bg-[#ff8000] text-white rounded-md hover:opacity-90 text-sm font-medium"
            data-testid="simulate-lieferando"
          >
            Lieferando simulieren
          </button>
          <button
            onClick={() => simulateOrder('wolt')}
            className="px-3 py-2 bg-[#00c2e8] text-white rounded-md hover:opacity-90 text-sm font-medium"
            data-testid="simulate-wolt"
          >
            Wolt simulieren
          </button>
        </div>
      </div>

      {message && (
        <div
          className={`mb-4 p-4 rounded-lg ${
            message.type === 'success'
              ? 'bg-[hsl(var(--success))]/10 text-[hsl(var(--success))] border border-[hsl(var(--success))]'
              : 'bg-[hsl(var(--error))]/10 text-[hsl(var(--error))] border border-[hsl(var(--error))]'
          }`}
        >
          {message.text}
        </div>
      )}

      <div className="dark-card rounded-lg shadow-sm border border-[hsl(var(--border))]">
        {loading ? (
          <div className="p-12 text-center text-[hsl(var(--muted-foreground))]">Lade Bestellungen...</div>
        ) : orders.length === 0 ? (
          <div className="p-12 text-center text-[hsl(var(--muted-foreground))]">Keine Bestellungen vorhanden</div>
        ) : (
          <div className="divide-y divide-[hsl(var(--border))]" data-testid="portal-orders-list">
            {orders.map((order) => (
              <div key={order.id} className="p-6 hover:bg-[hsl(var(--surface))] transition-colors">
                <div className="flex items-start justify-between">
                  <div className="flex-1">
                    <div className="flex items-center space-x-3 mb-2">
                      {getPlatformBadge(order.platform)}
                      <span className="font-medium">{order.ext_order_id}</span>
                      <span className={`px-2 py-1 rounded-full text-xs font-medium ${
                        order.status === 'accepted' 
                          ? 'bg-[hsl(var(--success))]/10 text-[hsl(var(--success))]'
                          : 'bg-[hsl(var(--warning))]/10 text-[hsl(var(--warning))]'
                      }`}>
                        {order.status === 'accepted' ? 'Angenommen' : 'Ausstehend'}
                      </span>
                    </div>

                    <div className="text-sm text-[hsl(var(--muted-foreground))] mb-3">
                      {order.customer?.name} • {order.customer?.address}
                    </div>

                    <div className="space-y-1">
                      {order.items?.map((item, idx) => (
                        <div key={idx} className="text-sm">
                          {item.qty}x {item.name}
                        </div>
                      ))}
                    </div>

                    {order.fees && order.fees.length > 0 && (
                      <div className="mt-2 text-xs text-[hsl(var(--muted-foreground))]">
                        Gebühren: {order.fees.map(f => f.name).join(', ')}
                      </div>
                    )}
                  </div>

                  <div className="text-right">
                    {order.status === 'pending' && (
                      <button
                        onClick={() => acceptOrder(order.id)}
                        disabled={processing === order.id}
                        className="px-4 py-2 bg-[hsl(var(--success))] text-white rounded-md hover:opacity-90 disabled:opacity-50 font-medium"
                        data-testid={`accept-order-${order.id}`}
                      >
                        {processing === order.id ? 'Verarbeite...' : 'Annehmen'}
                      </button>
                    )}
                    {order.status === 'accepted' && order.transaction_id && (
                      <div className="text-sm text-[hsl(var(--success))]">
                        ✓ Fiskalisiert
                      </div>
                    )}
                  </div>
                </div>
              </div>
            ))}
          </div>
        )}
      </div>
    </div>
  );
}

export default PortalOrders;
